import { Partner, CreatePartnerRequest, UpdatePartnerRequest } from '../types/partners';
import { getToken } from '../utils/auth';

// Resolve API base: use absolute VITE_API_BASE when provided; otherwise current origin.
const _configured = (import.meta as any).env?.VITE_API_BASE as string | undefined;
const API_BASE_URL = _configured && /^https?:\/\//i.test(_configured) ? _configured : window.location.origin;

// Helper function to make authenticated requests
const makeAuthenticatedRequest = async (url: string, options: RequestInit = {}) => {
  const token = getToken();

  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}/api${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    if (response.status === 401) {
      console.error('Unauthorized! Token may be invalid or expired');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export const partnersApi = {
  // Get all partners
  getAll: async (includeInactive = false): Promise<Partner[]> => {
    return makeAuthenticatedRequest(`/Sports/partners?includeInactive=${includeInactive}`);
  },

  // Get partner by ID
  getById: async (id: number): Promise<Partner> => {
    return makeAuthenticatedRequest(`/Sports/partners/${id}`);
  },

  // Create new partner
  create: async (partner: CreatePartnerRequest): Promise<Partner> => {
    return makeAuthenticatedRequest('/Sports/partners', {
      method: 'POST',
      body: JSON.stringify(partner),
    });
  },

  // Update partner
  update: async (id: number, partner: UpdatePartnerRequest): Promise<Partner> => {
    return makeAuthenticatedRequest(`/Sports/partners/${id}`, {
      method: 'PUT',
      body: JSON.stringify(partner),
    });
  },

  // Toggle partner active status
  toggleActive: async (id: number): Promise<Partner> => {
    return makeAuthenticatedRequest(`/Sports/partners/${id}/toggle-active`, {
      method: 'PATCH',
    });
  },

  // Check if partner can be deleted
  canDelete: async (id: number): Promise<boolean> => {
    const result = await makeAuthenticatedRequest(`/Sports/partners/${id}/canDelete`);
    return result.canDelete;
  },

  // Delete partner
  delete: async (id: number): Promise<void> => {
    await makeAuthenticatedRequest(`/Sports/partners/${id}`, {
      method: 'DELETE',
    });
  },
};
